import { useMemo } from 'react';

import { definedAccessor, xAxisAccessor as x, y0, y1 } from './accessors.js';
import { getBandStyle } from './band-style.js';
import { AreaCanvas } from '../../../../core/components/canvas/components/AreaCanvas.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { DEFAULT_STROKE_WIDTH_BAND_CHART } from '../../../../core/constants.js';
import { TimeseriesUnit } from '../../../../core/types/timeseries.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { useSeriesCurve } from '../../../hooks/useSeriesCurve.js';
import { useTimeseriesFilteredData } from '../../../hooks/useTimeseriesFilteredData.js';
import { useYScales } from '../../../hooks/useYScales.js';
import {
  TimeseriesBandDataPoint,
  TimeseriesBandSlotProps,
} from '../../../types/timeseries-band.js';
import { getYScaleByUnit } from '../../../utils/yAxis.js';
import { useDisplayConfigContext } from '../DisplayConfigContext.js';
import { BaseDisplayProps } from '../shared/types.js';

export interface BandChartDisplayProps extends BaseDisplayProps {
  series: TimeseriesBandSlotProps[];
}

/*
 * Displays the passed series as stacked band chart. The series are stacked based on their order, starting with the first series rendered at the bottom.
 */
export const BandChartDisplay = ({
  series: timeseries,
  xScale,
}: BandChartDisplayProps) => {
  const yScale = useYScales();
  const { width, height } = useGridPlotSize();
  const { highlightedSeriesIds } = useDisplayConfigContext();
  const { seriesMap, datapointsMap } = useTimeseriesFilteredData();
  const curve = useSeriesCurve();

  const bandSeries = useMemo(() => [...timeseries].reverse(), [timeseries]);

  const yScaleByUnit = (unit: TimeseriesUnit) => getYScaleByUnit(unit, yScale);

  const displayScatterPlot = (
    _datapoints: TimeseriesBandDataPoint[],
    _index: number,
  ) => false;

  return (
    <div data-testid="band-chart-display">
      {bandSeries.map((series) => {
        const seriesMetadata = safeGet(seriesMap, series);

        const { color, stroke, opacity } = getBandStyle(
          seriesMetadata,
          highlightedSeriesIds,
          series.strokeOnly,
        );

        const strokeColor = stroke ?? '';

        return (
          <AreaCanvas
            key={seriesMetadata.id}
            data={series.datapoints}
            canvasWidth={width}
            canvasHeight={height}
            x={x(xScale, datapointsMap)}
            y1={y1(yScaleByUnit, seriesMetadata, datapointsMap)}
            y0={y0(yScaleByUnit, seriesMetadata, datapointsMap)}
            color={() => color}
            strokeColor={() => strokeColor}
            defined={(dataPoint) => definedAccessor(dataPoint, datapointsMap)}
            opacity={opacity}
            displayScatterPlot={displayScatterPlot}
            strokeWidth={DEFAULT_STROKE_WIDTH_BAND_CHART}
            curve={curve}
            enableDotOverflow={false}
          />
        );
      })}
    </div>
  );
};

BandChartDisplay['displayName'] = 'BandChartDisplay';
